/**
 * Konum (enlem/boylam) derece–dakika–yön biçimi (ADR 0054 §3). Saklama ondalık derecedir
 * (`lat`, `lon`); giriş ve gösterim köprüdeki biçimdir: enlem `DD° MM.m' N/S`, boylam
 * `DDD° MM.m' E/W`. Boylam derecesi üç hanedir, baştaki sıfırlarla
 * [IMO Res. A.851(20) Ek C: "4-digit group … latitude", "5-digit group … longitude"]; NMEA 0183
 * `ddmm.m` / `dddmm.m` aynı.
 */

export type PositionAxis = 'lat' | 'lon';
export type Hemisphere = 'N' | 'S' | 'E' | 'W';

export interface PositionParts {
  /** Tam derece, işaretsiz. */
  degrees: number;
  /** Dakika, en çok bir ondalık (0–59.9). */
  minutes: number;
  hemisphere: Hemisphere;
}

/** Formdaki ham parçalar (kutulardaki metin). */
export interface PositionTextParts {
  degrees: string;
  minutes: string;
  hemisphere: string;
}

interface AxisRule {
  max: number;
  /** Derecenin hane sayısı (baştaki sıfırlarla). */
  width: number;
  /** Pozitif ve negatif yön. */
  hemispheres: readonly [Hemisphere, Hemisphere];
}

export const POSITION_AXES: Record<PositionAxis, AxisRule> = {
  lat: { max: 90, width: 2, hemispheres: ['N', 'S'] },
  lon: { max: 180, width: 3, hemispheres: ['E', 'W'] },
};

const EPSILON = 1e-9;

/** Ondalık dereceden DM parçalarına; dakika 0.1'e yuvarlanır, 60'a ulaşırsa dereceye taşınır. */
export function toPositionParts(value: number, axis: PositionAxis): PositionParts | null {
  const rule = POSITION_AXES[axis];
  if (!Number.isFinite(value) || Math.abs(value) > rule.max) return null;
  const abs = Math.abs(value);
  let degrees = Math.floor(abs);
  let tenths = Math.round((abs - degrees) * 600);
  if (tenths >= 600) {
    degrees += 1;
    tenths = 0;
  }
  const hemisphere = value < 0 ? rule.hemispheres[1] : rule.hemispheres[0];
  return { degrees, minutes: tenths / 10, hemisphere };
}

/** DM parçalarından ondalık dereceye; kurala uymayan parça `null`. */
export function fromPositionParts(parts: PositionParts, axis: PositionAxis): number | null {
  const rule = POSITION_AXES[axis];
  const { degrees, minutes, hemisphere } = parts;
  if (!Number.isInteger(degrees) || degrees < 0 || degrees > rule.max) return null;
  if (!Number.isFinite(minutes) || minutes < 0 || minutes >= 60) return null;
  if (Math.abs(minutes * 10 - Math.round(minutes * 10)) > EPSILON) return null;
  // 90° enlemde ve 180° boylamda dakika olmaz
  if (degrees === rule.max && minutes !== 0) return null;
  if (!rule.hemispheres.includes(hemisphere)) return null;
  const value = degrees + minutes / 60;
  if (value === 0) return 0;
  return hemisphere === rule.hemispheres[1] ? -value : value;
}

/** 34.76 → `34° 45.6' N`; 99.21 → `099° 12.6' E`; `null` → boş. */
export function formatPosition(value: number | null | undefined, axis: PositionAxis): string {
  if (value === null || value === undefined) return '';
  const parts = toPositionParts(value, axis);
  if (!parts) return '';
  const degrees = String(parts.degrees).padStart(POSITION_AXES[axis].width, '0');
  const minutes = parts.minutes.toFixed(1).padStart(4, '0');
  return `${degrees}° ${minutes}' ${parts.hemisphere}`;
}

const TEXT = /^\s*(\d*)\s*°?\s*(\d*(?:[.,]\d*)?)\s*'?\s*([NSEWnsew]?)\s*$/;

/** Metni kutulara ayırır (yön yoksa eksenin pozitif yönü). Biçimsiz metin boş kutular olur. */
export function splitPositionText(text: string, axis: PositionAxis): PositionTextParts {
  const fallback = POSITION_AXES[axis].hemispheres[0];
  const match = TEXT.exec(text);
  if (!match) return { degrees: '', minutes: '', hemisphere: fallback };
  return {
    degrees: match[1] ?? '',
    minutes: match[2] ?? '',
    hemisphere: (match[3] ?? '').toUpperCase() || fallback,
  };
}

/** Kutulardan kanonik metne. Derece ve dakika boşsa boş metin (konum girilmedi). */
export function joinPositionText(parts: PositionTextParts): string {
  const degrees = parts.degrees.trim();
  const minutes = parts.minutes.trim();
  if (degrees === '' && minutes === '') return '';
  return `${degrees}° ${minutes}' ${parts.hemisphere}`;
}

/** Derece kutusu alandan çıkınca: baştaki sıfırlar (`99` → `099`); rakam değilse olduğu gibi. */
export function padPositionDegrees(text: string, axis: PositionAxis): string {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return text;
  return trimmed.padStart(POSITION_AXES[axis].width, '0');
}

/** Dakika kutusu alandan çıkınca: `5` → `05.0`, `5,3` → `05.3`; biçimsizse olduğu gibi. */
export function padPositionMinutes(text: string): string {
  const trimmed = text.trim();
  if (!/^\d{1,2}(?:[.,]\d)?$/.test(trimmed)) return text;
  return Number(trimmed.replace(',', '.')).toFixed(1).padStart(4, '0');
}

const STRICT = /^\s*(\d{1,3})\s*°?\s+(\d{1,2}(?:[.,]\d)?)\s*'?\s*([NSEWnsew])\s*$/;

/**
 * Kanonik ya da boşluklu metinden (`34° 45.6' N`, `34 45.6 N`) ondalık dereceye. Ondalık derece,
 * eksik parça, fazla ondalık ve sınır dışı değer `null` (form `INVALID_POSITION` gösterir).
 */
export function parsePosition(text: string, axis: PositionAxis): number | null {
  const match = STRICT.exec(text);
  if (!match) return null;
  const degreesText = match[1] ?? '';
  if (degreesText.length > POSITION_AXES[axis].width) return null;
  return fromPositionParts(
    {
      degrees: Number(degreesText),
      minutes: Number((match[2] ?? '').replace(',', '.')),
      hemisphere: (match[3] ?? '').toUpperCase() as Hemisphere,
    },
    axis,
  );
}

/** Formdaki metnin durumu: boş, geçersiz ya da geçerli (`null`). */
export function positionTextIssue(
  text: string,
  axis: PositionAxis,
): 'EMPTY' | 'INVALID_POSITION' | null {
  if (text.trim() === '') return 'EMPTY';
  return parsePosition(text, axis) === null ? 'INVALID_POSITION' : null;
}
